import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useHighlights } from '@/hooks/useHighlights';
import { useWorldSelectionSheet } from '@/components/worlds/useWorldSelectionSheet';
import { useSubscriptionState } from '@/hooks/useSubscriptionState';
import { useStoryWorlds } from '@/hooks/useStoryWorlds';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { PlanStatusCard } from '@/components/patterns/PlanStatusCard';
import { HighlightsCard } from '@/components/patterns/HighlightsCard';
import { SectionHeading } from '@/components/patterns/SectionHeading';
import { TipSection } from '@/components/patterns/TipSection';
import { WeltCard } from '@/components/patterns/WeltCard';
import { StoryCard } from '@/components/patterns/StoryCard';
import { EmptyStateBlock } from '@/components/patterns/EmptyStateBlock';
import {
  derivePlanStatusProps,
  deriveLifecyclePlanStatusProps,
} from '@/features/subscription/derive-plan-status-props';
import { createPortalSession, reactivateSubscription } from '@/features/subscription/api';
import { fetchStoriesList } from '@/features/story/api';
import { getCurrentTip } from '@/features/inspiration/data/tips';
import { ApiError, NetworkError } from '@/lib/api-client';
import { WORLDS, type WorldSlug } from '@/lib/worlds';
import { getHeroCover } from '@/lib/worldAssets';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';
import type { StoryListItem } from '@/features/story/types';

/**
 * Start-Page / Welcome-Cockpit (Batch 7).
 *
 * Sönkes Figma page-welcome (node 8-5651). Default state
 * (hasStories=true) ships in this commit; empty-state branch
 * (hasStories=false) + fetch-error banner land in Commit 3.
 *
 * Layout per Figma (responsive):
 *   - Mobile: vertical stack, 20px page padding.
 *   - Tablet: 40px padding, Plan + Tip cards become a 2-column row.
 *   - Desktop: Sönke's `desktop-max-container` caps the inner content
 *     at 768px and centers. ContentContainer's default (1100px) is
 *     overridden inline at this call-site per Gate-1 D4 (single-use
 *     override; promote to a variant only if a second 768px-cap
 *     page appears).
 *
 * PlanStatusCard is state-agnostic (data-prop API per Gate-1 F2).
 * StartRoute derives props from `useEntitlement()` via
 * `derivePlanStatusProps` — see Briefing-conformance note in Gate-1
 * for why the 3 lifecycle states (pending_cancel / past_due /
 * pending_downgrade) are deferred to Batch 7.1 follow-up.
 */
export function StartRoute() {
  const navigate = useNavigate();
  const firstName = useAuthStore((s) => s.user?.firstName ?? '');
  const { entitlement } = useEntitlement({ refetchOnMount: true });
  const { state: subscriptionState, refetch: refetchSubscriptionState } = useSubscriptionState({
    refetchOnMount: true,
  });
  const { getWorldSlugById } = useStoryWorlds();
  const highlights = useHighlights();
  // Batch 10 Sub-phase D — Plan-Status-Card "Geschichte erstellen"
  // CTA opens the global WorldSelectionSheet instead of navigating to
  // the (deleted) bare /builder route.
  const { openSheet } = useWorldSelectionSheet();

  const [stories, setStories] = useState<StoryListItem[] | null>(null);
  const [storiesError, setStoriesError] = useState<string | null>(null);

  // Single fetch function reused by the initial-mount effect AND the
  // error-banner "Erneut versuchen" button. useCallback keeps the
  // reference stable so the effect only runs once on mount.
  // .then-chained instead of async/await so setState calls live in
  // callback context — satisfies react-hooks/set-state-in-effect when
  // invoked from the mount-effect below.
  // Batch 8 envelope: extract `.items` from `{ items, total }` —
  // Start-Page only needs items; total is consumed by useStoryCount()
  // / Library subtitle elsewhere.
  const loadStories = useCallback(() => {
    return fetchStoriesList({ limit: 3 })
      .then(({ items }) => {
        setStories(items);
        setStoriesError(null);
      })
      .catch((err: unknown) => {
        console.error(err);
        if (err instanceof ApiError || err instanceof NetworkError) {
          setStoriesError(STRINGS.startPage.errors.storiesLoadFailed);
        }
      });
  }, []);

  useEffect(() => {
    void loadStories();
  }, [loadStories]);

  // Mini-Batch-7.1 reconnect (Batch 9A Commit 4): when a lifecycle
  // event is active on the user's subscription, the PlanStatusCard
  // priority-switches from credits-derive (entitlement) to lifecycle-
  // derive (subscriptionState). Resolves PWA/Offen #25.
  //
  // Action handlers mirror AccountRoute (A-01) — same Stripe portal
  // redirect / direct reactivate semantics. Errors are silently logged
  // here; Start-Page surface is a low-friction entry point and a full
  // banner would be visually heavy for an event the user would also
  // see on Account-Page. Account-Page's local-error UI is the primary
  // surface for action failures.
  const handleStartUpdatePaymentMethod = async () => {
    try {
      const { url } = await createPortalSession('payment_method_update');
      window.location.assign(url);
    } catch (err) {
      console.error(err);
    }
  };
  const handleStartReactivate = async () => {
    try {
      await reactivateSubscription();
      window.setTimeout(() => {
        void refetchSubscriptionState();
      }, 500);
    } catch (err) {
      console.error(err);
    }
  };
  const handleStartCancelPendingPlanChange = async () => {
    try {
      const { url } = await createPortalSession('subscription_update');
      window.location.assign(url);
    } catch (err) {
      console.error(err);
    }
  };

  const lifecycleProps = deriveLifecyclePlanStatusProps(subscriptionState, {
    onUpdatePaymentMethod: () => void handleStartUpdatePaymentMethod(),
    onReactivate: () => void handleStartReactivate(),
    onCancelPendingPlanChange: () => void handleStartCancelPendingPlanChange(),
  });
  // Batch 10 Sub-phase F — wire useSubscriptionState into derive
  // options so Active-Sub users see the proper "{tierLabel}-Abo" +
  // "{used} von {total} diesen Monat" framing per Decision #16. Closes
  // the Sub-phase B fallback to "Abo" + pack-style count documented
  // there as a temporary state.
  const planProps =
    lifecycleProps ??
    derivePlanStatusProps(entitlement, navigate, {
      chooseplanTarget: '/account',
      onCreateStoryClick: openSheet,
      subscriptionTierLabel: subscriptionState?.tierLabel ?? null,
      subscriptionStoriesPerMonth: subscriptionState?.storiesPerMonth ?? null,
    });

  // Empty-state is reached only AFTER a successful fetch returned zero
  // rows. Pending (stories === null, no error) and errored
  // (stories === null, storiesError != null) both keep the default
  // Returning-User chrome — per Gate-1 Sebastian override, we don't
  // flip to New-User until we've confirmed the user has no stories.
  const isEmptyState = stories !== null && stories.length === 0;
  const greetingVariant: 'returning' | 'newUser' = isEmptyState ? 'newUser' : 'returning';

  const handleWeltClick = (slug: WorldSlug) => navigate(`/builder/${slug}`);
  const handleStoryClick = (id: string) => navigate(`/story/${id}`);
  const handleAllStoriesClick = () => navigate('/library');
  const handleRetry = () => {
    void loadStories();
  };

  return (
    // Full-viewport white wrapper matches Sönkes page-welcome frame
    // (`bg-[var(--white,white)]` on node 8:5650). Sits outside
    // ContentContainer so the bg extends to the viewport edges on
    // desktop — ContentContainer caps at 1100px, which would otherwise
    // let AppShell's `bg-dream-background` (yellow-light) show through
    // left + right of the capped content. Pattern matches StoryRoute's
    // self-bg convention. See AppShell audit tracker entry PWA #28.
    <div className="min-h-screen bg-dream-white">
      <ContentContainer>
        <div className="flex flex-col gap-5 py-5 md:gap-7 md:pt-10 md:pb-14 desktop:mx-auto desktop:max-w-[768px]">
          <Greeting firstName={firstName} variant={greetingVariant} />

          <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
            <PlanStatusCard {...planProps} className="md:flex-1" testId="plan-status-card" />
            {isEmptyState ? (
              <TipSection
                heading={STRINGS.startPage.welcome.heading}
                body={STRINGS.startPage.welcome.body}
                className="md:flex-1"
                testId="welcome-section"
              />
            ) : (
              (() => {
                // SI-Inspiration 2026-05-11 (Sebastian Gate-1 Decision Q1
                // Option A2): wire Start-Tab to getCurrentTip() so the lead
                // tip on Start matches the lead Card on Inspiration-Tab.
                // STRINGS.startPage.tip.heading is repurposed as the
                // SectionHeading label above the card (post-verify follow-
                // up 2026-05-11); .linkLabel still drives the "Mehr Tipps
                // im Inspiration-Tab" CTA below the rotating body.
                const tip = getCurrentTip();
                return (
                  <SectionHeading
                    heading={STRINGS.startPage.tip.heading}
                    id="start-page-tip"
                    className="md:flex-1"
                  >
                    <TipSection
                      heading={`${tip.emoji ?? '💡'} ${tip.title}`}
                      body={tip.body}
                      link={{
                        label: STRINGS.startPage.tip.linkLabel,
                        to: '/inspiration',
                      }}
                      testId="tip-section"
                    />
                  </SectionHeading>
                );
              })()
            )}
          </div>

          {highlights && <HighlightsCard data={highlights} testId="highlights-card" />}

          <WorldsSection onSelect={handleWeltClick} />

          <LatestStoriesList
            stories={stories}
            error={storiesError}
            onRetry={handleRetry}
            getWorldSlugById={getWorldSlugById}
            onStoryClick={handleStoryClick}
            onAllClick={handleAllStoriesClick}
          />
        </div>
      </ContentContainer>
    </div>
  );
}

// ── Greeting ───────────────────────────────────────────────────

interface GreetingProps {
  firstName: string;
  variant: 'returning' | 'newUser';
}

function Greeting({ firstName, variant }: GreetingProps) {
  // Batch 10 Sub-phase F — Decision #7 (Briefing wording wins).
  // Single-line interpolation pattern replaces the prior 2-line
  // (welcome line + firstName below) layout. Returning variant uses
  // `'Hi {firstName}'` template; newUser variant shows the briefing-
  // locked `'💡 Willkommen!'` heading with no firstName interpolation
  // (the welcome card below carries the body copy).
  const G = STRINGS.startPage.greeting;
  let text: string;
  if (variant === 'newUser') {
    text = G.newUserHeading;
  } else if (firstName.trim().length > 0) {
    text = G.returningTemplate.replace('{firstName}', firstName);
  } else {
    text = G.returningFallback;
  }

  return (
    <div
      className="flex w-full flex-col items-start justify-center gap-1 text-base leading-4 text-dream-navy"
      data-testid="greeting"
    >
      <p className={variant === 'newUser' ? 'font-semibold' : 'font-normal'}>{text}</p>
    </div>
  );
}

// ── Worlds ─────────────────────────────────────────────────────

/**
 * Welten-Section. Hero SVGs delivered by Sönke 2026-04-26 and wired
 * via `getHeroCover(slug)` (Batch 8 Library bug-fix commit). The
 * gradient placeholder fallback was removed once the SVGs landed —
 * `WeltCard.coverSrc` falls through to `cover-default.svg` for
 * unknown slugs, which is a tighter contract than the prior
 * per-slug gradient lookup.
 */
function WorldsSection({ onSelect }: { onSelect: (slug: WorldSlug) => void }) {
  return (
    <SectionHeading heading={STRINGS.startPage.worlds.heading} id="start-worlds-heading">
      <div className="flex w-full items-start gap-2 md:gap-3">
        {(Object.values(WORLDS) as Array<(typeof WORLDS)[keyof typeof WORLDS]>).map((w) => (
          <WeltCard
            key={w.slug}
            slug={w.slug}
            name={w.name}
            coverSrc={getHeroCover(w.slug)}
            onClick={() => onSelect(w.slug)}
            testId={`welt-card-${w.slug}`}
          />
        ))}
      </div>
    </SectionHeading>
  );
}

// ── Latest-Stories list ───────────────────────────────────────

interface LatestStoriesListProps {
  stories: StoryListItem[] | null;
  error: string | null;
  onRetry: () => void;
  getWorldSlugById: (id: string) => string | null;
  onStoryClick: (id: string) => void;
  onAllClick: () => void;
}

/**
 * Renders one of 4 branches below the section heading:
 *
 *   - Error banner (stories === null && error set) — placed between
 *     heading and would-be card area per Gate-1 D5. Carries a retry
 *     button that fires `onRetry` to re-attempt the fetch.
 *   - StoryCard list (stories.length > 0) — 3 rows plus "Alle meine
 *     Geschichten anzeigen" link to /library.
 *   - EmptyStateBlock (stories !== null && length === 0) — Lucide
 *     BookOpen icon (Gate-1 D3) + heading + body per Sönkes
 *     section-nostories frame. No CTA; the WeltCards above + the
 *     BottomNav "+" provide the creation path.
 *   - Nothing (stories === null && no error) — fetch still pending;
 *     neutral state acceptable per Gate-1.
 */
function LatestStoriesList({
  stories,
  error,
  onRetry,
  getWorldSlugById,
  onStoryClick,
  onAllClick,
}: LatestStoriesListProps) {
  const hasStories = stories !== null && stories.length > 0;
  const isEmpty = stories !== null && stories.length === 0;

  return (
    <SectionHeading heading={STRINGS.startPage.stories.heading} id="start-stories-heading">
      {error && <StoriesLoadErrorBanner message={error} onRetry={onRetry} />}

      {!error && hasStories && (
        <>
          <div className="flex w-full flex-col items-start gap-2">
            {stories.map((s) => (
              <StoryCardRow
                key={s.id}
                story={s}
                getWorldSlugById={getWorldSlugById}
                onClick={() => onStoryClick(s.id)}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onAllClick}
            className={cn(
              'w-full text-center text-xs font-semibold leading-3 text-dream-navy underline decoration-solid',
            )}
          >
            {STRINGS.startPage.stories.allLink}
          </button>
        </>
      )}

      {!error && isEmpty && (
        <EmptyStateBlock
          icon={
            <BookOpen
              className="h-[46px] w-[46px] text-dream-gold"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          }
          heading={STRINGS.startPage.emptyStories.heading}
          body={STRINGS.startPage.emptyStories.body}
          testId="empty-stories-block"
        />
      )}
    </SectionHeading>
  );
}

interface StoriesLoadErrorBannerProps {
  message: string;
  onRetry: () => void;
}

function StoriesLoadErrorBanner({ message, onRetry }: StoriesLoadErrorBannerProps) {
  return (
    <div
      role="alert"
      data-testid="stories-error-banner"
      className="flex w-full flex-col gap-2 rounded-lg bg-error/10 p-3 md:flex-row md:items-center md:justify-between"
    >
      <p className="text-xs text-dream-navy">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 self-start text-xs font-semibold text-dream-navy underline md:self-auto"
      >
        {STRINGS.startPage.errors.retry}
      </button>
    </div>
  );
}

interface StoryCardRowProps {
  story: StoryListItem;
  getWorldSlugById: (id: string) => string | null;
  onClick: () => void;
}

function StoryCardRow({ story, getWorldSlugById, onClick }: StoryCardRowProps) {
  const slug = getWorldSlugById(story.worldId);
  const worldSlug: WorldSlug = slug === 'kp' || slug === 'sb' ? slug : 'aa';
  const worldName = WORLDS[worldSlug.toUpperCase() as Uppercase<WorldSlug>].name;
  return (
    <StoryCard
      density="start"
      title={story.title}
      childName={story.childName}
      worldName={worldName}
      worldSlug={worldSlug}
      createdDate={formatGermanShortDate(story.createdAt)}
      onClick={onClick}
      testId={`story-card-${story.id}`}
    />
  );
}

function formatGermanShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}

// ── PlanStatusCard data derivation ────────────────────────────
// `derivePlanStatusProps` extracted to
// `pwa/src/features/subscription/derive-plan-status-props.ts` in Batch 9A
// so AccountRoute (A-01) can reuse the same Credits-states derivation.
// StartRoute imports from the shared module above.
